import React, { useEffect, useMemo, useState } from "react";
import { Alert, DatePicker, Form, Modal, Select, TimePicker, message } from "antd";
import dayjs from "dayjs";
import { fetchSprNumberList } from "../../sprs/number/spr-number-api";
import { docExpMovePlacement, fetchBronFreeNumbers } from "./doc-exp-api";

const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj?.[k] != null) return obj[k];
  }
  return undefined;
};

/**
 * Упрощённое переселение: с даты — новый номер до конца периода.
 * Delphi: UActRegRazmMove.
 */
const DocExpMoveModal = ({ open, idDoc, row, onCancel, onDone }) => {
  const [form] = Form.useForm();
  const [numbers, setNumbers] = useState([]);
  const [freeIds, setFreeIds] = useState(null);
  const [freeLoading, setFreeLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchSprNumberList()
      .then((list) => {
        const items = Array.isArray(list) ? list : pick(list, "items", "Items") || [];
        setNumbers(
          items
            .map((n) => ({
              value: pick(n, "idNumber", "IdNumber"),
              label: pick(n, "name", "Name"),
              childCount: pick(n, "childCount", "ChildCount") || 0
            }))
            .filter((n) => n.value && !n.childCount)
        );
        const beg = row?.dateBeg ? dayjs(row.dateBeg) : dayjs();
        const end = row?.dateEnd ? dayjs(row.dateEnd) : dayjs().add(1, "day");
        form.setFieldsValue({
          fromDate: beg,
          toDate: end,
          idNumber: row?.idNumber || undefined,
          timeEnd: dayjs(end.format("HH:mm") === "00:00" ? "12:00" : end.format("HH:mm"), "HH:mm")
        });
      })
      .catch((e) => message.error(e.message || "Ошибка"));
  }, [open, form, row]);

  const fromDate = Form.useWatch("fromDate", form);
  const toDate = Form.useWatch("toDate", form);
  const fromKey = fromDate?.isValid?.() ? fromDate.format("YYYY-MM-DD") : "";
  const toKey = toDate?.isValid?.() ? toDate.format("YYYY-MM-DD") : "";

  useEffect(() => {
    if (!open || !fromKey || !toKey || dayjs(toKey).isBefore(dayjs(fromKey), "day")) {
      setFreeIds(null);
      return undefined;
    }
    const ac = new AbortController();
    const t = setTimeout(() => {
      setFreeLoading(true);
      fetchBronFreeNumbers({ dateBeg: fromKey, dateEnd: toKey, exceptDoc: idDoc }, ac.signal)
        .then((ids) => {
          if (ac.signal.aborted) return;
          const set = new Set((Array.isArray(ids) ? ids : []).map(Number));
          setFreeIds(set);
          const current = form.getFieldValue("idNumber");
          if (current && !set.has(current)) form.setFieldValue("idNumber", undefined);
        })
        .catch((e) => {
          if (ac.signal.aborted) return;
          setFreeIds(null);
          message.error(e.message || "Не удалось получить свободные номера");
        })
        .finally(() => {
          if (!ac.signal.aborted) setFreeLoading(false);
        });
    }, 250);
    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [open, idDoc, fromKey, toKey, form]);

  const options = useMemo(
    () => (freeIds ? numbers.filter((n) => freeIds.has(n.value)) : numbers),
    [numbers, freeIds]
  );

  const onOk = async () => {
    try {
      const values = await form.validateFields();
      const from = values.fromDate.startOf("day");
      const to = values.toDate.startOf("day");
      if (to.isBefore(from, "day")) {
        message.error("Дата окончания раньше начала");
        return;
      }
      if (freeIds && !freeIds.has(values.idNumber)) {
        message.error("Номер занят на выбранный период");
        return;
      }
      const days = [];
      for (let d = from; !d.isAfter(to, "day"); d = d.add(1, "day")) {
        days.push({
          fdate: d.format("YYYY-MM-DD"),
          idNumber: values.idNumber
        });
      }
      setSubmitting(true);
      const saved = await docExpMovePlacement(idDoc, {
        days,
        timeEnd: values.timeEnd.format("HH:mm:ss")
      });
      message.success("Размещение изменено");
      onDone?.(saved);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || "Не удалось изменить размещение");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Изменение размещения"
      open={open}
      onCancel={onCancel}
      onOk={onOk}
      confirmLoading={submitting}
      okButtonProps={{ disabled: freeLoading }}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="fromDate"
          label="С даты"
          rules={[{ required: true, message: "Укажите дату" }]}
        >
          <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="toDate"
          label="По дату"
          rules={[{ required: true, message: "Укажите дату" }]}
        >
          <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="idNumber"
          label="Номер"
          rules={[{ required: true, message: "Выберите номер" }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            options={options}
            loading={freeLoading}
            placeholder={freeLoading ? "Проверяем свободные номера…" : "Выберите номер"}
            notFoundContent={freeLoading ? "Проверяем…" : "Нет свободных номеров на период"}
          />
        </Form.Item>
        <Form.Item name="timeEnd" label="Время выезда" rules={[{ required: true }]}>
          <TimePicker format="HH:mm" style={{ width: "100%" }} />
        </Form.Item>
      </Form>
      {freeIds && fromKey && toKey ? (
        <Alert
          type="info"
          showIcon
          message={`Показаны только номера, свободные с ${dayjs(fromKey).format("DD.MM.YYYY")} по ${dayjs(toKey).format("DD.MM.YYYY")} (${options.length})`}
        />
      ) : null}
    </Modal>
  );
};

export default DocExpMoveModal;
